import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';

const COLLEAGUE_FEEDBACK_API = '/umbraco/api/ColleagueFeedback';

type ApiEnvelope<T> = {
  Code?: number;
  data: T;
  message?: string;
};

type BaseFeedbackItem = {
  id: number;
  ratings: Required<ColleagueFeedbackRatings>;
  feedback: string;
  submittedAt: string;
};

type PagedResponse<T> = {
  page: number;
  pageSize: number;
  totalCount: number;
  items: T[];
};

export type ColleagueFeedbackRatings = {
  teamwork: number;
  communication: number;
  helpfulness: number;
  reliability: number;
};

export type SubmitColleagueFeedbackRequest = {
  targetId: string;
  ratings: ColleagueFeedbackRatings;
  feedback?: string;
};

export type SubmitColleagueFeedbackResponse = {
  id: number;
};

export type RecentFeedbackItem = BaseFeedbackItem & {
  targetId: string;
  targetName: string;
};

export type AboutMeFeedbackItem = BaseFeedbackItem;

export type GetRecentFeedbackParams = {
  page?: number;
  pageSize?: number;
};

export type GetRecentFeedbackResponse = PagedResponse<RecentFeedbackItem>;

export type GetAboutMeFeedbackResponse = PagedResponse<AboutMeFeedbackItem>;

export type ParticipantFeedbackContext = {
  ReviewedEmployeeName: string;
  PeriodFrom: string;
  PeriodTo: string;
};

export const colleagueFeedbackApi = createApi({
  reducerPath: 'colleagueFeedbackApi',
  baseQuery,
  tagTypes: ['ColleagueFeedbackRecent'],
  endpoints: (builder) => ({
    getParticipantFeedbackContext: builder.query<ParticipantFeedbackContext, number>({
      query: (reviewId) => ({
        url: `${COLLEAGUE_FEEDBACK_API}/GetContext`,
        params: { reviewId },
      }),
      transformResponse: (response: ApiEnvelope<ParticipantFeedbackContext>) => {
        if (response.Code === 1 && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load review feedback context');
      },
    }),
    submitColleagueFeedback: builder.mutation<
      SubmitColleagueFeedbackResponse,
      SubmitColleagueFeedbackRequest
    >({
      query: (body) => ({
        url: `${COLLEAGUE_FEEDBACK_API}/Post`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<SubmitColleagueFeedbackResponse>) => {
        if (response.Code === 1) return response.data;
        throw new Error(response.message || 'Failed to submit colleague feedback');
      },
      invalidatesTags: [
        { type: 'ColleagueFeedbackRecent', id: 'LIST' },
        { type: 'ColleagueFeedbackRecent', id: 'ABOUT_ME' },
      ],
    }),

    getRecentColleagueFeedback: builder.query<
      GetRecentFeedbackResponse,
      GetRecentFeedbackParams | void
    >({
      query: (params) => ({
        url: `${COLLEAGUE_FEEDBACK_API}/Recent`,
        params: params ?? {},
      }),
      transformResponse: (response: ApiEnvelope<GetRecentFeedbackResponse>) => {
        if (response.Code === 1) return response.data;
        throw new Error(response.message || 'Failed to load recent colleague feedback');
      },
      providesTags: [{ type: 'ColleagueFeedbackRecent', id: 'LIST' }],
      keepUnusedDataFor: 60,
    }),

    getAboutMeColleagueFeedback: builder.query<
      GetAboutMeFeedbackResponse,
      GetRecentFeedbackParams | void
    >({
      query: (params) => ({
        url: `${COLLEAGUE_FEEDBACK_API}/AboutMe`,
        params: params ?? {},
      }),
      transformResponse: (response: ApiEnvelope<GetAboutMeFeedbackResponse>) => {
        if (response.Code === 1) return response.data;
        throw new Error(response.message || 'Failed to load feedback about me');
      },
      providesTags: [{ type: 'ColleagueFeedbackRecent', id: 'ABOUT_ME' }],
      keepUnusedDataFor: 60,
    }),
  }),
});

export const {
  useGetParticipantFeedbackContextQuery,
  useSubmitColleagueFeedbackMutation,
  useGetRecentColleagueFeedbackQuery,
  useGetAboutMeColleagueFeedbackQuery,
} = colleagueFeedbackApi;
